import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {
  calculateLeadTime,
  formatCurrency,
  formatLeadTime,
  formatValueForExcel,
} from './tableUtils';

// Enhanced PDF Export using jsPDF-autotable for better control
export const exportToPDF = async (
  sectionSelections,
  sortedWeeks,
  weekColumns,
  groupedJobs,
  setSaveStatus,
  setShowSaveBanner
) => {
  setSaveStatus("Generating PDF export...");
  setShowSaveBanner(true);

  try {
    const selectedColumns = sectionSelections.columns || {};
    
    // Configure jsPDF for tabloid landscape
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: [792, 1224] // 11x17 inches in points
    });

    // Add title
    pdf.setFontSize(16);
    pdf.setTextColor(0, 85, 165);
    pdf.text('Watertronics Job Schedule Report', 40, 30);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 1100, 30);

    // Build headers and column configs
    const headers = buildHeadersArray(selectedColumns);
    const columnConfigs = buildColumnConfigs(headers);

    // Build table data
    let tableData = [];
    let rowIndex = 0;

    sortedWeeks.forEach((week) => {
      const weekJobs = groupedJobs[week] || [];
      if (weekJobs.length === 0) return;

      // Add week header
      const dateRange = getWeekDateRange(week, weekColumns);
      const formattedTotal = formatCurrency(calculateWeekTotal(weekJobs));
      const weekHeaderText = week === "Unscheduled" 
        ? `${week} : Total Value: ${formattedTotal}`
        : `${week} ${dateRange} : Total Value: ${formattedTotal}`;

      // Add week header as a full-width row
      const weekRow = new Array(headers.length).fill('');
      weekRow[0] = weekHeaderText;
      tableData.push(weekRow);

      // Add jobs for this week
      weekJobs.forEach((job, index) => {
        const rowData = buildJobRowData(job, selectedColumns, rowIndex + index + 1);
        tableData.push(rowData);
      });

      rowIndex += weekJobs.length + 1;
    });

    // Generate table with autoTable
    pdf.autoTable({
      head: [headers],
      body: tableData,
      startY: 50,
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [0, 85, 165],
        textColor: [255, 255, 255],
        fontSize: 6,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'bottom',
        cellPadding: { top: 15, right: 2, bottom: 2, left: 2 },
        minCellHeight: 80
      },
      columnStyles: columnConfigs,
      didParseCell: function(data) {
        // Rotate headers 90 degrees
        if (data.section === 'head') {
          data.cell.styles.cellPadding = { top: 2, right: 2, bottom: 15, left: 2 };
          data.cell.styles.valign = 'bottom';
          data.cell.styles.halign = 'center';
          data.cell.styles.minCellHeight = 80;
        }
        
        // Style week headers
        if (data.section === 'body' && data.row.index >= 0) {
          const cellValue = data.cell.raw;
          if (typeof cellValue === 'string' && 
              (cellValue.includes('Week') || cellValue.includes('Unscheduled')) && 
              data.column.index === 0) {
            data.cell.colSpan = headers.length;
            data.cell.styles.fillColor = [0, 85, 165];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          }
        }
      },
      didDrawCell: function(data) {
        // Custom draw for rotated headers
        if (data.section === 'head') {
          const text = data.cell.text[0];
          const cellHeight = data.cell.height;
          const cellWidth = data.cell.width;
          
          // Clear the cell
          pdf.setFillColor(0, 85, 165);
          pdf.rect(data.cell.x, data.cell.y, cellWidth, cellHeight, 'F');
          
          // Draw rotated text
          pdf.saveGraphicsState();
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(6);
          
          // Translate to cell center and rotate
          const centerX = data.cell.x + cellWidth / 2;
          const centerY = data.cell.y + cellHeight - 5;
          
          pdf.text(text, centerX, centerY, {
            angle: 90,
            align: 'center',
            baseline: 'bottom'
          });
          
          pdf.restoreGraphicsState();
          
          // Don't draw the default text
          return false;
        }
      },
      margin: { top: 50, left: 20, right: 20, bottom: 20 },
      tableWidth: 'auto',
      showHead: 'firstPage'
    });

    // Save PDF
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const filename = `Watertronics_Job_Schedule_${dateStr}.pdf`;
    
    pdf.save(filename);
    
    setSaveStatus("PDF export completed successfully!");
    setTimeout(() => setShowSaveBanner(false), 3000);

  } catch (error) {
    console.error("Error exporting to PDF:", error);
    setSaveStatus(`Error exporting to PDF: ${error.message}`);
    setTimeout(() => setShowSaveBanner(false), 5000);
  }
};

// Enhanced Excel Export with formatting
export const exportToExcelWithFormatting = (
  sectionSelections,
  sortedWeeks,
  weekColumns,
  groupedJobs,
  setSaveStatus,
  setShowSaveBanner
) => {
  setSaveStatus("Preparing formatted Excel export...");
  setShowSaveBanner(true);

  try {
    const wb = XLSX.utils.book_new();
    const columnSelections = sectionSelections.columns || {};
    
    // Build headers array
    const selectedHeaders = buildHeadersArray(columnSelections);
    
    // Create data array with proper formatting
    const excelData = [selectedHeaders];
    
    // Process each week section
    sortedWeeks.forEach((week, weekIndex) => {
      // Add week header row
      const dateRange = getWeekDateRange(week, weekColumns);
      const weekJobs = groupedJobs[week] || [];
      const formattedTotal = formatCurrency(calculateWeekTotal(weekJobs));
      
      const weekHeaderText = week === "Unscheduled" 
        ? `${week} : Total Value: ${formattedTotal}`
        : `${week} ${dateRange} : Total Value: ${formattedTotal}`;
      
      excelData.push([weekHeaderText]);
      
      // Add job rows for this week
      weekJobs.forEach((job, index) => {
        const rowData = buildJobRowData(job, columnSelections, index + 1);
        excelData.push(rowData);
      });
      
      // Add empty row between weeks (except last)
      if (weekIndex < sortedWeeks.length - 1) {
        excelData.push([]);
      }
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Apply formatting
    applyExcelFormatting(ws, excelData, selectedHeaders.length);
    
    // Set column widths
    ws['!cols'] = calculateColumnWidths(selectedHeaders, excelData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Job Schedule");
    
    // Generate filename and save
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const filename = `Watertronics_Job_Schedule_Formatted_${dateStr}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    
    setSaveStatus("Formatted Excel export completed successfully!");
    setTimeout(() => setShowSaveBanner(false), 3000);

  } catch (error) {
    console.error("Error exporting to Excel:", error);
    setSaveStatus(`Error exporting to Excel: ${error.message}`);
    setTimeout(() => setShowSaveBanner(false), 5000);
  }
};

// Enhanced Print function with proper scaling
export const printTableOptimized = (
  sectionSelections,
  sortedWeeks,
  weekColumns,
  groupedJobs,
  pumpDetails,
  setSaveStatus,
  setShowSaveBanner
) => {
  try {
    const selectedColumns = sectionSelections.columns || {};
    
    // Create print window
    const printWindow = window.open('', '_blank', 'width=1400,height=900');
    
    // Get current date
    const currentDate = new Date().toLocaleDateString();
    
    // Clone and prepare table
    const originalTable = document.querySelector('.job-schedule-table');
    if (!originalTable) {
      throw new Error('Could not find job schedule table');
    }
    
    const tableClone = originalTable.cloneNode(true);
    filterTableForExport(tableClone, selectedColumns);
    enforcePrintableColumnWidths(tableClone);
    
    // Generate optimized print HTML - pass the outerHTML string, not the element
    const printHTML = generateOptimizedPrintHTML(tableClone.outerHTML, currentDate, selectedColumns);
    
    // Write to print window
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Auto-print with delay for styling
    setTimeout(() => {
      printWindow.print();
    }, 1000);
    
    setSaveStatus("Print view generated successfully!");
    setTimeout(() => setShowSaveBanner(false), 3000);

  } catch (error) {
    console.error('Error generating print view:', error);
    setSaveStatus(`Error generating print view: ${error.message}`);
    setTimeout(() => setShowSaveBanner(false), 5000);
  }
};

// Helper Functions

function filterTableForExport(tableClone, selectedColumns) {
  console.log('Starting table filtering for export');

  // Job number cells include extra project/model context for the compact touch view.
  // That context is hidden by the app stylesheet, which is not present in the print window.
  tableClone.querySelectorAll('.schedule-compact-job-context').forEach((element) => {
    element.remove();
  });

  const thead = tableClone.querySelector('thead');
  if (!thead) {
    return tableClone;
  }

  const headerRows = Array.from(thead.querySelectorAll('tr'));
  if (headerRows.length === 0) {
    return tableClone;
  }

  const flattenedColumns = [];

  if (headerRows.length === 1) {
    Array.from(headerRows[0].querySelectorAll('th')).forEach((header, index) => {
      flattenedColumns.push({ index, header });
    });
  } else {
    const firstRowHeaders = Array.from(headerRows[0].querySelectorAll('th'));
    const detailHeaders = Array.from(headerRows[1].querySelectorAll('th'));
    let columnIndex = 0;
    let detailHeaderIndex = 0;

    firstRowHeaders.forEach((header) => {
      const colSpan = Number.parseInt(header.getAttribute('colspan') || '1', 10);
      const rowSpan = Number.parseInt(header.getAttribute('rowspan') || '1', 10);

      if (rowSpan >= 2) {
        flattenedColumns.push({ index: columnIndex, header });
      } else {
        for (let offset = 0; offset < colSpan; offset += 1) {
          const detailHeader = detailHeaders[detailHeaderIndex];
          if (detailHeader) {
            flattenedColumns.push({ index: columnIndex + offset, header: detailHeader });
          }
          detailHeaderIndex += 1;
        }
      }

      columnIndex += colSpan;
    });
  }

  const hasSelectedColumns = Object.values(selectedColumns || {}).some(selected => selected);
  const columnsToKeep = flattenedColumns.filter(({ header }) => {
    const isActionColumn = header.classList.contains('action-column')
      || Boolean(header.querySelector('.bi-pencil, button'));
    if (isActionColumn) return false;

    if (!hasSelectedColumns) return true;

    if (header.classList.contains('row-number-column')) return true;

    const columnId = header.getAttribute('data-column-id');
    return Boolean(columnId && selectedColumns[columnId]);
  });

  const keptIndexes = new Set(columnsToKeep.map(({ index }) => index));
  const bodyRows = Array.from(tableClone.querySelectorAll('tbody tr:not(.week-separator)'));

  bodyRows.forEach((row) => {
    Array.from(row.children).forEach((cell, index) => {
      if (!keptIndexes.has(index)) {
        cell.remove();
      }
    });
  });

  tableClone.querySelectorAll('tbody tr.week-separator td').forEach((cell) => {
    cell.setAttribute('colspan', String(columnsToKeep.length));
  });

  const newHeaderRow = document.createElement('tr');
  columnsToKeep
    .sort((a, b) => a.index - b.index)
    .forEach(({ header }) => {
      const headerClone = header.cloneNode(true);
      headerClone.removeAttribute('rowspan');
      headerClone.removeAttribute('colspan');
      newHeaderRow.appendChild(headerClone);
    });

  const newThead = document.createElement('thead');
  newThead.appendChild(newHeaderRow);
  thead.parentNode.replaceChild(newThead, thead);

  console.log('Filtering columns based on selections:', selectedColumns);
  return tableClone;
}

function applySimplifiedPDFStyling(tableClone) {
  console.log('Applying simplified PDF styling');
  
  // Basic table styling
  tableClone.style.cssText = `
    width: 100% !important;
    border-collapse: collapse !important;
    font-family: Arial, sans-serif !important;
    font-size: 8pt !important;
    background: white !important;
  `;

  // Style headers - keep them simple and horizontal
  const headers = tableClone.querySelectorAll('th');
  headers.forEach(header => {
    header.style.cssText = `
      background-color: #0055a5 !important;
      color: white !important;
      font-weight: bold !important;
      font-size: 8pt !important;
      border: 1px solid #333 !important;
      padding: 4px !important;
      text-align: center !important;
      white-space: nowrap !important;
      transform: none !important;
      writing-mode: horizontal-tb !important;
    `;
  });

  // Style data cells
  const cells = tableClone.querySelectorAll('td');
  cells.forEach(cell => {
    cell.style.cssText = `
      border: 1px solid #333 !important;
      padding: 3px !important;
      font-size: 7pt !important;
      white-space: nowrap !important;
      text-align: left !important;
    `;
    
    // Special styling for notes column to allow wrapping
    if (cell.classList.contains('notes-column')) {
      cell.style.cssText += `
        white-space: normal !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        min-width: 200px !important;
        max-width: 400px !important;
        width: 300px !important;
        vertical-align: top !important;
        line-height: 1.2 !important;
      `;
    }
    
    // Special styling for week headers
    if (cell.classList.contains('week-header') || cell.textContent.includes('Week')) {
      cell.style.cssText += `
        background-color: #0055a5 !important;
        color: white !important;
        font-weight: bold !important;
        text-align: center !important;
      `;
    }
  });

  console.log('PDF styling applied');
}

const PRINT_MIN_COLUMN_WIDTH_PX = 30;
const PRINT_MIN_HEADER_WIDTH_PX = 30;

function enforcePrintableColumnWidths(tableElement) {
  if (!tableElement) {
    return;
  }

  const headers = Array.from(tableElement.querySelectorAll('thead th'));
  const bodyRows = Array.from(tableElement.querySelectorAll('tbody tr'));

  headers.forEach((header, index) => {
    if (
      header.classList.contains('department-header') ||
      header.classList.contains('week-header') ||
      header.classList.contains('notes-column') ||
      header.classList.contains('job-name-column') ||
      header.classList.contains('model-number-column') ||
      header.classList.contains('job-number-column') ||
      header.classList.contains('row-number-column')
    ) {
      return;
    }

    header.style.minWidth = `${PRINT_MIN_HEADER_WIDTH_PX}px`;
    header.style.width = `${PRINT_MIN_HEADER_WIDTH_PX}px`;

    bodyRows.forEach((row) => {
      if (row.classList.contains('week-separator')) {
        return;
      }

      const cell = row.children[index];
      if (!cell || cell.classList.contains('notes-column')) {
        return;
      }

      cell.style.minWidth = `${PRINT_MIN_COLUMN_WIDTH_PX}px`;
      cell.style.width = `${PRINT_MIN_COLUMN_WIDTH_PX}px`;
    });
  });
}

export function buildHeadersArray(columnSelections) {
  const selectedHeaders = ["#"]; // Always include row number

  // Add selected base columns
  if (columnSelections.jobNumber) selectedHeaders.push("Job Number");
  if (columnSelections.modelNumber) selectedHeaders.push("Model Number");
  if (columnSelections.projectName) selectedHeaders.push("Project Name");
  if (columnSelections.orderDate) selectedHeaders.push("Order Date");
  if (columnSelections.productionRelease) selectedHeaders.push("Production Release");
  if (columnSelections.releaseMeetingDate) selectedHeaders.push("Release Meeting Date");

  // Add selected engineering columns
  if (columnSelections.mechanicalEngineer) selectedHeaders.push("Mechanical Engineer");
  if (columnSelections.electricalEngineer) selectedHeaders.push("Electrical Engineer");
  if (columnSelections.controlsEngineer) selectedHeaders.push("Controls Engineer");
  if (columnSelections.mechanicalDue) selectedHeaders.push("Mechanical Due");
  if (columnSelections.mechanicalDone) selectedHeaders.push("Mechanical Done");
  if (columnSelections.electricalDue) selectedHeaders.push("Electrical Due");
  if (columnSelections.electricalDone) selectedHeaders.push("Electrical Done");

  // Add selected fabrication columns
  if (columnSelections.npPipeFabDue) selectedHeaders.push("NP Pipe Fab Due");
  if (columnSelections.npPipeFabDone) selectedHeaders.push("NP Pipe Fab Done");
  if (columnSelections.fusionBondRequired) selectedHeaders.push("Fusion Bond Required");
  if (columnSelections.fabDue) selectedHeaders.push("Fab Due");
  if (columnSelections.fabDone) selectedHeaders.push("Fab Done");
  if (columnSelections.fabExpectedCompletion) selectedHeaders.push("Fab Expected Completion");

  // Add selected control panel columns
  if (columnSelections.softwareDue) selectedHeaders.push("Software Due");
  if (columnSelections.softwareDone) selectedHeaders.push("Software Done");
  if (columnSelections.controlPanelDue) selectedHeaders.push("Control Panel Due");
  if (columnSelections.controlPanelDone) selectedHeaders.push("Control Panel Done");
  if (columnSelections.controlPanelExpectedCompletion) selectedHeaders.push("Control Panel Expected Completion");

  // Add selected pumps columns
  if (columnSelections.hcPumpsDue) selectedHeaders.push("HC Pumps Due");
  if (columnSelections.hcPumpsDone) selectedHeaders.push("HC Pumps Done");
  if (columnSelections.vtPumpsDue) selectedHeaders.push("VT Pumps Due");
  if (columnSelections.pumpsDone) selectedHeaders.push("VT Pumps Done");
  if (columnSelections.pumpExpectedCompletion) selectedHeaders.push("Pump Expected Completion");

  // Add selected assembly columns
  if (columnSelections.assemblyDue) selectedHeaders.push("Assembly Due");
  if (columnSelections.assemblyDone) selectedHeaders.push("Assembly Done");
  if (columnSelections.assemblyExpectedCompletion) selectedHeaders.push("Assembly Expected Completion");

  // Add selected test columns
  if (columnSelections.testDue) selectedHeaders.push("Test Due");
  if (columnSelections.testDone) selectedHeaders.push("Test Done");
  if (columnSelections.testExpectedCompletion) selectedHeaders.push("Test Expected Completion");

  // Add selected North Prairie columns
  if (columnSelections.npCsmDue) selectedHeaders.push("NP CSM Due");
  if (columnSelections.npCsmDone) selectedHeaders.push("NP CSM Done");
  if (columnSelections.npSuctionLineDue) selectedHeaders.push("NP Suction Line Due");
  if (columnSelections.npSuctionLineDone) selectedHeaders.push("NP Suction Line Done");
  if (columnSelections.npInducerTubeDue) selectedHeaders.push("NP Inducer Tube Due");
  if (columnSelections.npInducerTubeDone) selectedHeaders.push("NP Inducer Tube Done");
  if (columnSelections.npBmxDue) selectedHeaders.push("NP BMX Due");
  if (columnSelections.npBmxDone) selectedHeaders.push("NP BMX Done");

  // Add selected shipping columns
  if (columnSelections.packDue) selectedHeaders.push("Pack Due");
  if (columnSelections.transportType) selectedHeaders.push("Transport Type");
  if (columnSelections.originalScheduledShipDate) selectedHeaders.push("Original Scheduled Ship Date");
  if (columnSelections.currentPlanShipDate) selectedHeaders.push("Current Plan Ship Date");
  if (columnSelections.actualShipDate) selectedHeaders.push("Actual Ship Date");
  if (columnSelections.scheduledArrivalDate) selectedHeaders.push("Scheduled Arrival Date");

  // Add selected portal details columns
  if (columnSelections.salesRep) selectedHeaders.push("Sales Rep");
  if (columnSelections.jobLocation) selectedHeaders.push("Job Location");

  // Add selected additional details columns
  if (columnSelections.notes) selectedHeaders.push("Notes");
  if (columnSelections.paymentTerms) selectedHeaders.push("Payment Terms");
  if (columnSelections.dollarValue) selectedHeaders.push("Dollar Value");
  if (columnSelections.leadTime) selectedHeaders.push("Lead Time");

  return selectedHeaders;
}

export function buildJobRowData(job, columnSelections, rowNumber) {
  const rowData = [rowNumber];

  // Add selected base columns in the same order as headers
  if (columnSelections.jobNumber) rowData.push(formatValueForExcel("JobNumber", job.JobNumber));
  if (columnSelections.modelNumber) rowData.push(formatValueForExcel("ModelNumber", job.ModelNumber));
  if (columnSelections.projectName) rowData.push(formatValueForExcel("ProjectName", job.ProjectName));
  if (columnSelections.orderDate) rowData.push(formatValueForExcel("OrderDate", job.OrderDate));
  if (columnSelections.productionRelease) rowData.push(formatValueForExcel("ProductionRelease", job.ProductionRelease));
  if (columnSelections.releaseMeetingDate) rowData.push(formatValueForExcel("ReleaseDate", job.ReleaseDate));

  // Engineering columns
  if (columnSelections.mechanicalEngineer) rowData.push(formatValueForExcel("MechanicalEngineer", job.MechanicalEngineer));
  if (columnSelections.electricalEngineer) rowData.push(formatValueForExcel("ElectricalEngineer", job.ElectricalEngineer));
  if (columnSelections.controlsEngineer) rowData.push(formatValueForExcel("ControlsEngineer", job.ControlsEngineer));
  if (columnSelections.mechanicalDue) rowData.push(formatValueForExcel("MechanicalDue", job.MechanicalDue));
  if (columnSelections.mechanicalDone) rowData.push(formatValueForExcel("MechanicalDone", job.MechanicalDone));
  if (columnSelections.electricalDue) rowData.push(formatValueForExcel("ElectricalDue", job.ElectricalDue));
  if (columnSelections.electricalDone) rowData.push(formatValueForExcel("ElectricalDone", job.ElectricalDone));

  // Fabrication columns
  if (columnSelections.npPipeFabDue) rowData.push(formatValueForExcel("NPPipeFabDue", job.NPPipeFabDue));
  if (columnSelections.npPipeFabDone) rowData.push(formatValueForExcel("NPPipeFabDone", job.NPPipeFabDone));
  if (columnSelections.fusionBondRequired) rowData.push(formatValueForExcel("FusionBondRequired", job.FusionBondRequired));
  if (columnSelections.fabDue) rowData.push(formatValueForExcel("FabDue", job.FabDue));
  if (columnSelections.fabDone) rowData.push(formatValueForExcel("FabDone", job.FabDone));
  if (columnSelections.fabExpectedCompletion) rowData.push(formatValueForExcel("FabExpectedCompletion", job.FabExpectedCompletion));

  // Control Panel columns
  if (columnSelections.softwareDue) rowData.push(formatValueForExcel("SoftwareDue", job.SoftwareDue));
  if (columnSelections.softwareDone) rowData.push(formatValueForExcel("SoftwareDone", job.SoftwareDone));
  if (columnSelections.controlPanelDue) rowData.push(formatValueForExcel("ControlPanelDue", job.ControlPanelDue));
  if (columnSelections.controlPanelDone) rowData.push(formatValueForExcel("ControlPanelDone", job.ControlPanelDone));
  if (columnSelections.controlPanelExpectedCompletion) rowData.push(formatValueForExcel("ControlPanelExpectedCompletion", job.ControlPanelExpectedCompletion));

  // Pumps columns
  if (columnSelections.hcPumpsDue) rowData.push(formatValueForExcel("HCPumpsDue", job.HCPumpsDue));
  if (columnSelections.hcPumpsDone) rowData.push(formatValueForExcel("HCPumpsDone", job.HCPumpsDone));
  if (columnSelections.vtPumpsDue) rowData.push(formatValueForExcel("VTPumpsDue", job.VTPumpsDue));
  if (columnSelections.pumpsDone) rowData.push(formatValueForExcel("PumpDone", job.PumpDone));
  if (columnSelections.pumpExpectedCompletion) rowData.push(formatValueForExcel("PumpExpectedCompletion", job.PumpExpectedCompletion));

  // Assembly columns
  if (columnSelections.assemblyDue) rowData.push(formatValueForExcel("AssemblyDue", job.AssemblyDue));
  if (columnSelections.assemblyDone) rowData.push(formatValueForExcel("AssemblyDone", job.AssemblyDone));
  if (columnSelections.assemblyExpectedCompletion) rowData.push(formatValueForExcel("AssemblyExpectedCompletion", job.AssemblyExpectedCompletion));

  // Test columns
  if (columnSelections.testDue) rowData.push(formatValueForExcel("TestDue", job.TestDue));
  if (columnSelections.testDone) rowData.push(formatValueForExcel("TestDone", job.TestDone));
  if (columnSelections.testExpectedCompletion) rowData.push(formatValueForExcel("TestExpectedCompletion", job.TestExpectedCompletion));

  // North Prairie columns
  if (columnSelections.npCsmDue) rowData.push(formatValueForExcel("NPCSMDue", job.NPCSMDue));
  if (columnSelections.npCsmDone) rowData.push(formatValueForExcel("NPCSMDone", job.NPCSMDone));
  if (columnSelections.npSuctionLineDue) rowData.push(formatValueForExcel("NPSuctionLineDue", job.NPSuctionLineDue));
  if (columnSelections.npSuctionLineDone) rowData.push(formatValueForExcel("NPSuctionLineDone", job.NPSuctionLineDone));
  if (columnSelections.npInducerTubeDue) rowData.push(formatValueForExcel("NPInducerTubeDue", job.NPInducerTubeDue));
  if (columnSelections.npInducerTubeDone) rowData.push(formatValueForExcel("NPInducerTubeDone", job.NPInducerTubeDone));
  if (columnSelections.npBmxDue) rowData.push(formatValueForExcel("NPBMXDue", job.NPBMXDue));
  if (columnSelections.npBmxDone) rowData.push(formatValueForExcel("NPBMXDone", job.NPBMXDone));

  // Shipping columns
  if (columnSelections.packDue) rowData.push(formatValueForExcel("PackDue", job.PackDue));
  if (columnSelections.transportType) rowData.push(formatValueForExcel("TransportType", job.TransportType));
  if (columnSelections.originalScheduledShipDate) rowData.push(formatValueForExcel("OriginalScheduledShipDate", job.OriginalScheduledShipDate));
  if (columnSelections.currentPlanShipDate) rowData.push(formatValueForExcel("CurrentPlanShipDate", job.CurrentPlanShipDate));
  if (columnSelections.actualShipDate) rowData.push(formatValueForExcel("ActualShipDate", job.ActualShipDate));
  if (columnSelections.scheduledArrivalDate) rowData.push(formatValueForExcel("ScheduledArrivalDate", job.ScheduledArrivalDate));

  // Portal Details columns
  if (columnSelections.salesRep) rowData.push(formatValueForExcel("SalesRep", job.SalesRep));
  if (columnSelections.jobLocation) rowData.push(formatValueForExcel("JobLocation", job.JobLocation));

  // Additional details columns
  if (columnSelections.notes) rowData.push(formatValueForExcel("Notes", job.Notes));
  if (columnSelections.paymentTerms) rowData.push(formatValueForExcel("PaymentTerms", job.PaymentTerms));
  if (columnSelections.dollarValue) rowData.push(formatCurrency(job.DollarValue) || "");
  if (columnSelections.leadTime) {
    rowData.push(formatLeadTime(calculateLeadTime(
      job.ProductionRelease,
      job.CurrentPlanShipDate
    )));
  }

  return rowData;
}

function applyExcelFormatting(ws, excelData, headerCount) {
  // Apply cell formatting for better appearance
  const range = XLSX.utils.decode_range(ws['!ref']);
  
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;

      // Format header row
      if (R === 0) {
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "0055A5" } },
          font: { color: { rgb: "FFFFFF" }, bold: true },
          alignment: { horizontal: "center" }
        };
      }
      
      // Format week header rows (rows that span all columns)
      else if (excelData[R] && excelData[R].length === 1) {
        ws[cellAddress].s = {
          fill: { fgColor: { rgb: "0055A5" } },
          font: { color: { rgb: "FFFFFF" }, bold: true },
          alignment: { horizontal: "center" }
        };
      }
    }
  }
}

function calculateColumnWidths(headers, data) {
  return headers.map((header, i) => {
    let maxWidth = header.length;
    for (let j = 1; j < data.length; j++) {
      if (data[j][i] && String(data[j][i]).length > maxWidth) {
        maxWidth = String(data[j][i]).length;
      }
    }
    return { width: Math.min(Math.max(maxWidth + 2, 10), 50) };
  });
}

function getWeekDateRange(week, weekColumns) {
  if (week === "Unscheduled") return "";
  
  const weekNumber = parseInt(week.replace("Week ", ""), 10);
  if (weekColumns[weekNumber - 1]) {
    const weekColumn = weekColumns[weekNumber - 1];
    const startDate = moment(weekColumn.startDate);
    const endDate = moment(weekColumn.endDate);
    return `(${weekColumn.isoWeekNum}): ${startDate.format('MM/DD')} - ${endDate.format('MM/DD')}`;
  }
  return "";
}

function calculateWeekTotal(jobs) {
  return jobs.reduce((total, job) => {
    const dollarValue = parseFloat(job.DollarValue) || 0;
    return total + dollarValue;
  }, 0);
}

// Updated generateOptimizedPrintHTML with consistent header rotation
function generateOptimizedPrintHTML(tableHTML, currentDate) {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Watertronics Job Schedule - Print</title>
      <!-- Include Bootstrap Icons CSS -->
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css">
      <style>
        /* Reset and base styles */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
          font-family: Arial, sans-serif;
          font-size: 8pt;
          margin: 0;
          padding: 0;
          background: white;
        }

        /* Page setup for tabloid landscape */
        @page {
          size: 11in 17in landscape;
          margin: 0.25in;
        }

        /* Print container */
        .print-container {
          width: 100%;
          max-width: 98%;
          margin: 0 auto;
        }

        /* Header styles */
        .print-header {
          text-align: center;
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 8px;
          color: #0055a5;
        }

        .print-date {
          text-align: right;
          font-size: 8pt;
          margin-bottom: 8px;
          color: #666;
        }

        /* Table styles optimized for tabloid print */
        .job-schedule-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 7pt;
          table-layout: auto;
          margin: 0;
        }

        .job-schedule-table th,
        .job-schedule-table td {
          border: 0.5pt solid #ccc;
          padding: 2pt 3pt;
          text-align: left;
          vertical-align: middle;
          overflow: hidden;
          min-width: 30px;
          width: 30px;
        }

        /* Rotated headers */
        .job-schedule-table th {
          background-color: #0055a5 !important;
          color: white !important;
          font-weight: bold;
          font-size: 6pt;
          text-align: center;
          height: 100px;
          position: relative;
          vertical-align: bottom;
          white-space: nowrap;
          padding: 0;
          min-width: 30px !important;
          width: 30px !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .job-schedule-table th > div {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%) rotate(-90deg);
          transform-origin: center bottom;
          white-space: nowrap;
          text-align: right;
          width: 70px;
        }

        /* Week header styles */
        .week-header {
          background-color: #0055a5 !important;
          color: white !important;
          font-weight: bold !important;
          font-size: 8pt !important;
          text-align: center !important;
          padding: 4pt !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Dynamic column widths based on content */
        .row-number-column { width: 25px !important; min-width: 25px !important; }
        .job-number-column { width: 60px !important; min-width: 60px !important; }
        .model-number-column { width: 80px !important; min-width: 80px !important; }
        .job-name-column { width: 150px !important; min-width: 150px !important; }
        .date-column { width: 30px !important; min-width: 30px !important; }
        .engineer-column { width: 30px !important; min-width: 30px !important; }
        .notes-column { width: 300px !important; min-width: 200px !important; max-width: 400px !important; }
        .dollar-column { width: 70px !important; min-width: 70px !important; }
        .standard-column { width: 30px !important; min-width: 30px !important; }

        /* Cell content handling */
        .job-schedule-table td {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Notes column specific styling for text wrapping */
        .job-schedule-table td.notes-column {
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          overflow: visible !important;
          text-overflow: clip !important;
          vertical-align: top !important;
          min-width: 200px !important;
          max-width: 400px !important;
          width: 300px !important;
          line-height: 1.2 !important;
        }

        .job-schedule-table td.date-column,
        .job-schedule-table td.row-number-column {
          text-align: center;
        }

        .job-schedule-table td.dollar-column {
          text-align: right;
        }

        /* Compact-only identity details must never appear inside printed Job Number cells. */
        .schedule-compact-job-context {
          display: none !important;
        }

        /* Preserve highlighting colors */
        .highlight-large-station { background-color: #e3f2fd !important; }
        .highlight-sky-harvester { background-color: #f3e5f5 !important; }
        .highlight-retrofit-kit { background-color: #e8f5e8 !important; }
        .highlight-watermax-3000 { background-color: #fff3e0 !important; }
        .highlight-watermax-5000 { background-color: #fce4ec !important; }
        .highlight-watermax-7000 { background-color: #e0f2f1 !important; }
        .highlight-watermax-9000 { background-color: #f1f8e9 !important; }

        /* Status colors */
        .on-time { color: #2e7d32 !important; font-weight: bold; }
        .late { color: #d32f2f !important; font-weight: bold; }

        /* Checkmark styles for done dates */
        .bi-check {
          color: #28a745 !important;
          font-size: 1.2em !important;
          margin-left: 0.25rem;
          font-weight: normal !important;
        }

        /* Long build icon styles */
        .bi-arrows-vertical {
          color: #ff8c00 !important;
          font-size: 1.0em !important;
          margin-left: 0.25rem;
          font-weight: normal !important;
        }

        /* Ensure colors print */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Hide elements that shouldn't print */
        .no-print {
          display: none !important;
        }

        /* Page break handling */
        .week-separator {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        tr {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        @media print {
          .job-schedule-table th > div {
            font-size: 6pt !important;
          }
          
          /* Ensure checkmarks are visible in print */
          .bi-check::before {
            content: "\\F26E" !important;
            font-family: "bootstrap-icons" !important;
            display: inline-block !important;
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color: #28a745 !important;
          }

          /* Ensure long build arrows are visible in print */
          .bi-arrows-vertical::before {
            content: "\\F118" !important;
            font-family: "bootstrap-icons" !important;
            display: inline-block !important;
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color: #ff8c00 !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-header">Watertronics Job Schedule</div>
        <div class="print-date">Generated: ${currentDate}</div>
        <div class="table-wrapper">
          ${tableHTML}
        </div>
      </div>
      <script>
        // Wait for Bootstrap Icons to load
        window.addEventListener('load', function() {
          // Process table headers more carefully
          const table = document.querySelector('.job-schedule-table');
          if (!table) return;
          
          const thead = table.querySelector('thead');
          if (!thead) return;
          
          // Get all header rows
          const headerRows = thead.querySelectorAll('tr');
          
          // Process headers based on the table structure
          if (headerRows.length > 1) {
            // Multi-row header structure
            // Process the second row which contains the actual column headers
            const actualHeaders = headerRows[headerRows.length - 1].querySelectorAll('th');
            actualHeaders.forEach(header => {
              // Only wrap if not already wrapped
              if (!header.querySelector('div')) {
                const text = header.innerText;
                header.innerHTML = '<div>' + text + '</div>';
              }
            });
            
            // Also process first row headers that have rowspan
            const firstRowHeaders = headerRows[0].querySelectorAll('th[rowspan="2"]');
            firstRowHeaders.forEach(header => {
              // Only wrap if not already wrapped
              if (!header.querySelector('div')) {
                const text = header.innerText;
                header.innerHTML = '<div>' + text + '</div>';
              }
            });
          } else {
            // Single row header structure
            const headers = thead.querySelectorAll('th');
            headers.forEach(header => {
              // Only wrap if not already wrapped
              if (!header.querySelector('div')) {
                const text = header.innerText;
                header.innerHTML = '<div>' + text + '</div>';
              }
            });
          }
          
          // Debug: Log what we're working with
          console.log('Processing table for checkmarks...');
          
          // Process each row to add checkmarks
          const tbody = table.querySelector('tbody');
          if (!tbody) return;
          
          const rows = tbody.querySelectorAll('tr');
          rows.forEach((row, rowIndex) => {
            // Skip week separator rows
            if (row.classList.contains('week-separator')) return;
            
            // Get all cells in this row
            const cells = Array.from(row.querySelectorAll('td'));
            
            // Process each cell
            cells.forEach((cell, cellIndex) => {
              // Check all classes on the cell
              const classNames = Array.from(cell.classList);
              
              // Find if this is a Due column
              const isDueColumn = classNames.some(className => 
                className.includes('Due') && !className.includes('Done')
              );
              
              if (isDueColumn) {
                // Get the base column name
                let dueColumnClass = classNames.find(className => 
                  className.includes('Due') && !className.includes('Done')
                );
                
                if (dueColumnClass) {
                  // Determine the corresponding Done column class
                  let doneColumnClass = dueColumnClass.replace('Due', 'Done');
                  
                  // Special handling for Pumps
                  if (dueColumnClass === 'HCPumpsDue') {
                    doneColumnClass = 'HCPumpsDone';
                  } else if (dueColumnClass === 'VTPumpsDue') {
                    doneColumnClass = 'PumpDone';
                  }
                  
                  // Find the done cell in the same row
                  const doneCell = row.querySelector('td.' + doneColumnClass);
                  
                  if (doneCell) {
                    const doneValue = doneCell.textContent.trim();
                    const dueValue = cell.textContent.trim();
                    
                    // If done has a value and due has a value, add checkmark
                    if (doneValue && doneValue !== '-' && doneValue !== '' &&
                        dueValue && dueValue !== '-' && dueValue !== '') {
                      // Add checkmark if not already present
                      if (!cell.querySelector('.bi-check')) {
                        console.log('Adding checkmark for', dueColumnClass, 'due:', dueValue, 'done:', doneValue);
                        cell.innerHTML = dueValue + ' <i class="bi bi-check"></i>';
                      }
                    }
                  }
                }
              }
            });
          });
          
          // Auto-print after a short delay
          setTimeout(() => {
            window.print();
          }, 800);
        });
      </script>
    </body>
    </html>
  `;
}

// Helper function to build column configurations for PDF with dynamic widths
function buildColumnConfigs(headers) {
  const configs = {};
  
  headers.forEach((header, index) => {
    const headerLower = header.toLowerCase();
    let width = 'auto';
    
    // Set specific widths based on column type - matching the CSS widths
    if (header === '#') width = 25;
    else if (headerLower.includes('job number')) width = 60;
    else if (headerLower.includes('model')) width = 80;
    else if (headerLower.includes('project name')) width = 150;
    else if (headerLower.includes('date') || headerLower.includes('due') || headerLower.includes('done')) width = 40;
    else if (headerLower.includes('engineer')) width = 50;
    else if (headerLower.includes('notes')) width = 300; // Increased width for notes to accommodate wrapping
    else if (headerLower.includes('dollar value')) width = 70;
    else width = 45;
    
    configs[index] = { cellWidth: width };
  });
  
  return configs;
}

// Main handler function for enhanced exports
export const handleEnhancedExport = (
  mode, // 'export' or 'print'
  sectionSelections,
  sortedWeeks,
  weekColumns,
  groupedJobs,
  setSaveStatus,
  setShowSaveBanner
) => {
  if (mode === 'print') {
    return printTableOptimized(
      sectionSelections,
      sortedWeeks,
      weekColumns,
      groupedJobs,
      setSaveStatus,
      setShowSaveBanner
    );
  } else if (mode === 'export') {
    const format = sectionSelections.format || 'excel';
    if (format === 'pdf') {
      return exportToPDF(
        sectionSelections,
        sortedWeeks,
        weekColumns,
        groupedJobs,
        setSaveStatus,
        setShowSaveBanner
      );
    } else {
      return exportToExcelWithFormatting(
        sectionSelections,
        sortedWeeks,
        weekColumns,
        groupedJobs,
        setSaveStatus,
        setShowSaveBanner
      );
    }
  }
};
